/**
 * EffectRegistry Service
 *
 * PURPOSE: Single source of truth for ALL active effects in the application
 * WHY: Unifies 4 separate effect systems (feats, items, spells, conditions) into one
 * INTEGRATION: Works alongside effectCalculator to manage and apply all stat modifications
 *
 * UNIVERSAL EFFECT SCHEMA:
 * [PHASE 1.3 - NOV 18, 2025]: Expanded with additional effect types for complete feat system
 * [STONE 3 / #101 — 2026-05-18]: Implementation-status column added below.
 *   Type names below are EITHER implemented (✅) or aspirational/v1.x (⚠️).
 *   Aspirational types may persist into save data via authoring UI but have
 *   NO runtime consumer; treat them as breadcrumbs, not contracts.
 *
 * {
 *   type: 'ability' | 'ac' | 'speed' | 'hp' | 'skill' | 'save' | 'attack' | 'damage' | 'initiative' |
 *         'carryCapacity' | 'proficiency' | 'resistance' | 'advantage' | 'sense' | 'armorDexCap' |
 *         'unarmedDamage' | 'weaponProperty' | 'combatAdvantage' | 'feature',
 *   target?: 'str' | 'dex' | 'con' | 'int' | 'wis' | 'cha' | [skill name] | [save type] | [weapon type] |
 *            [damage type] | [armor type] | [feature name] | [sense name],
 *   operation: 'add' | 'set' | 'multiply' | 'advantage' | 'disadvantage' | 'halve' | 'double' |
 *              'grant' | 'immunity' | 'ignore' | 'enable' | 'remove_disadvantage',
 *   value: number | string | boolean | Array,
 *   condition?: 'always' | {when: string, check: Function}
 * }
 *
 * Effects are indexed by `_getStatKey(effect)` which defaults to `effect.type`
 * (lowercased). Consumers query via `getEffectsForStat(typeKey, ...)`. There
 * is NO switch-case dispatcher on effect.type inside effectCalculator —
 * handlers are wired by passing the type string as the stat key.
 *
 * EFFECT TYPE USAGE GUIDE:
 * - ✅ ability: Modify ability scores (str, dex, con, int, wis, cha)
 * - ✅ ac: Modify Armor Class
 * - ✅ speed: Modify movement speed
 * - ✅ hp: Modify hit points or hit dice
 * - ✅ skill: Modify skill bonuses (target: skill name)
 * - ✅ save: Modify saving throws (target: ability)
 * - ✅ attack: Modify attack rolls
 * - ✅ damage: Modify damage rolls
 * - ✅ initiative: Modify initiative rolls
 * - ✅ carryCapacity: Modify carrying capacity
 * - ✅ proficiency: Grant proficiencies (value: ['weapon:martial', 'armor:heavy'])
 * - ✅ resistance: Grant damage resistance/immunity (value: damage type; operation: 'grant'|'immunity').
 *                  Consumer: EffectCalculator.getDamageResistances + getDamageResistanceDetails +
 *                  getDamageImmunities. CW Feat Creator authors via grantedResistances.
 * - ✅ advantage: Grant advantage on rolls (target: check name | 'all'; operation: 'advantage' |
 *                 'remove_disadvantage'). Consumer: EffectCalculator.hasAdvantageOnCheck +
 *                 hasDisadvantageRemoved. CW Feat Creator authors via grantedAdvantages.
 * - ✅ sense: Grant a sense at a range (target: 'darkvision'|'blindsight'|'tremorsense'|'truesight';
 *             value: range in feet; operation: 'grant'). Consumer: SensesMovementPanel via
 *             effectRegistry.getEffectsForStat('sense', ...). CW Feat Creator authors via
 *             grantedSenses. (No dedicated EffectCalculator method — consumer reads registry
 *             directly because senses don't need the calculator's aggregation pipeline.)
 * - ✅ armorDexCap: Modify armor dex cap (value: new cap)
 * - ✅ unarmedDamage: Modify unarmed attack damage (value: dice string)
 * - ⚠️ weaponProperty: Modify weapon properties (operation: 'ignore', value: property name) —
 *                      DECLARED but NO runtime consumer in v1. Aspirational; tracked under
 *                      COMING_SOON_ROADMAP as a v1.x infrastructure gap.
 * - ✅ combatAdvantage: Grant/remove combat advantage situations
 * - ✅ feature: Grant special features/abilities (value: feature name or array)
 *               [Phase 4b-infra APR 2026]: Consolidated former stealthAbility + perceptionModifier
 *               into 'feature' (rule-flag grants) and 'advantage' (disadvantage removal).
 *               Use EffectCalculator.hasFeatureFlag() to query.
 *
 * SPEC A/B/C/D EFFECT TYPES (added 2026-05-16 → 2026-05-17, not in original list above):
 * - ✅ spendAction: Generic action-economy spend (slot↔point, ki, rage). Spec B/C consumer
 *                   in spellCastingService + ResourceTracker.
 * - ✅ reactionTrigger: Reaction-window prompt declaration (Cutting Words, Shield).
 *                       Consumer: StatsPanel reactions section + effectCalculator action-surface
 *                       getters; CPI contributes via cpiContributions.
 * - ⚠️ cycleEndPicker: PLANNED rename of longRestPicker for cross-system generalization —
 *                      NOT YET IMPLEMENTED. `longRestPicker` is the live runtime key across ALL
 *                      consumers (effectCalculator, EffectBuilderControls, RestPanel, srdClassFeatures,
 *                      cpiContributions). Tracked: Sub-plan 4N (DOC-DRIFT-1, 2026-06-03).
 * - ✅ triggeredEffect: Fires on event sites (on_take_damage, on_turn_start, on_turn_end,
 *                       on_attack, on_damage). Consumer: applyOnTakeDamageTriggers + CombatTab
 *                       handleNext / handleHPAdjust. Spec C/D.
 * - ✅ concentrationAdvantage: War Caster shape — flagged advantage on CON saves while
 *                              concentrating. Consumer: concentrationSystem.hasAdvantage.
 * - ✅ damageBonus: Add-dice to weapon attacks. Consumer: monster actions, magic weapon
 *                   riders via effectRegistry.getEffectsForStat('damageBonus').
 * - ✅ damageReduction: Flat reduce-incoming-damage (Heavy Armor Master). Consumer:
 *                       effectCalculator.getDamageReduction.
 * - ✅ weaponAttackBonus: Monster + magic weapon +N to-hit. Consumer: MonsterEditor +
 *                         attack roll path.
 * - ✅ criticalRange: Expand crit range to 19-20 / 18-20. Consumer: attack roll path.
 * - ⚠️ resourceConversionMenu: Sorcerer slot↔point exchange — DECLARED in
 *                              srdClassFeatures.js but not implemented in v1. Tracked as
 *                              GAP 3 in compliance audit; v1.x.
 *
 * [HOMEBREW SYSTEM - JAN 10, 2026]: Homebrew-specific effect types
 * - numericRollMod: Flat +/- to rolls that stack (NOT advantage/disadvantage)
 *   WHY: Homebrew -4 penalty stacks differently than 5e disadvantage
 *   USAGE: { type: 'numericRollMod', target: 'perception', operation: 'add', value: -4 }
 * - missChance: Percentage miss chance (concealment)
 *   WHY: 3.5e concealment is NOT the same as 5e cover
 *   USAGE: { type: 'missChance', operation: 'add', value: 20 }
 * - actionDisable: Disable specific action types
 *   WHY: Custom conditions like "dazed" disable actions but not movement
 *   USAGE: { type: 'actionDisable', value: ['action', 'bonus_action'] }
 * - homebrewCondition: Reference to custom condition
 *   WHY: Link to condition definition in homebrewRules
 *   USAGE: { type: 'homebrewCondition', value: 'dazed' }
 *
 * Created: November 14, 2025
 * Part of: Master Unification Plan - Phase 0.1
 */

import { systemBus, EVENTS } from './systemBus';
import { SRD_CLASS_FEATURES } from '../data/srdClassFeatures';
import { resolveByLevelTable } from '../utils/byLevelTable';

class EffectRegistry {
  constructor() {
    // Map of source ID -> array of effects from that source
    // Example: 'feat:mobile' -> [{type: 'speed', operation: 'add', value: 10}]
    this.effects = new Map();

    // Map of stat -> array of all effects affecting it (for quick lookup)
    // Example: 'speed' -> [{source: 'feat:mobile', type: 'speed', ...}, ...]
    this.statIndex = new Map();

    // Cache for calculated values (cleared on any effect change)
    this.cache = new Map();
    this.cacheExpiry = 1000; // 1 second cache

    // Track registration history for debugging
    this.history = [];

    // [PHASE 2.2 - NOV 15, 2025]: Add metadata storage for D&D 5e spell stacking rules
    // PURPOSE: Track spell names, caster info, levels to enforce stacking rules
    // WHY: D&D 5e PHB p.205 - "The effects of the same spell cast multiple times don't combine"
    // STRUCTURE: sourceId -> {spellName, casterName, level, duration, timestamp}
    this.metadata = new Map();

    // Listen for state changes that should clear effects
    this._setupEventListeners();
  }

  /**
   * Register effects from a source
   * [PHASE 2.2 - NOV 15, 2025]: Updated to accept metadata for spell stacking prevention
   *
   * @param {string} sourceId - Unique identifier (e.g., 'feat:mobile', 'item:belt_giant_str', 'spell:bless')
   * @param {Array} effects - Array of effect objects following universal schema
   * @param {Object} metadata - Optional metadata {spellName, casterName, level, duration} for stacking rules
   */
  register(sourceId, effects, metadata = null) {
    // Validate source ID format
    if (!sourceId || typeof sourceId !== 'string') {
      console.error('[EffectRegistry] Invalid sourceId:', sourceId);
      return false;
    }

    // Validate effects array
    if (!Array.isArray(effects)) {
      console.error('[EffectRegistry] Effects must be an array:', effects);
      return false;
    }

    // [PHASE 2.2 - NOV 15, 2025]: Check D&D 5e spell stacking rules
    // WHY: Prevent duplicate spell effects (PHB p.205)
    if (metadata?.spellName) {
      const stackCheck = this._checkSpellStacking(metadata);
      if (!stackCheck.allowed) {
        console.warn(`[EffectRegistry] Spell stacking prevented: ${stackCheck.reason}`);
        // Emit event so UI can show message to user
        systemBus.emit(EVENTS.EFFECT_STACKING_PREVENTED, {
          spellName: metadata.spellName,
          reason: stackCheck.reason,
          conflictingSource: stackCheck.conflictingSource,
          conflictingMeta: stackCheck.conflictingMeta
        });
        return false; // Don't register - stacking prevented
      }
    }

    // Remove any existing effects from this source
    this.remove(sourceId);

    // Validate and normalize each effect
    const normalizedEffects = effects.map(effect => this._normalizeEffect(effect, sourceId));

    // Store effects by source
    this.effects.set(sourceId, normalizedEffects);

    // [PHASE 2.2]: Store metadata for stacking checks and future reference
    if (metadata) {
      this.metadata.set(sourceId, {
        ...metadata,
        timestamp: Date.now()
      });
    }

    // Update stat index
    normalizedEffects.forEach(effect => {
      const statKey = this._getStatKey(effect);
      if (!this.statIndex.has(statKey)) {
        this.statIndex.set(statKey, []);
      }
      this.statIndex.get(statKey).push({ source: sourceId, ...effect });
    });

    // Track in history
    // [ESR-007 FIX — MAY 2026]: Cap history at 100 most-recent entries (FIFO).
    //   Pre-fix this array grew unbounded; long sessions leaked memory.
    //   Mirrors systemBus.history. Capped after both push sites (register +
    //   remove) so the cap holds regardless of which path appends.
    this.history.push({
      timestamp: Date.now(),
      action: 'register',
      sourceId,
      effects: normalizedEffects,
      metadata // [PHASE 2.2]: Include metadata in history
    });
    if (this.history.length > 100) {
      this.history.splice(0, this.history.length - 100);
    }

    // Clear cache
    this.clearCache();

    // Emit event
    systemBus.emit(EVENTS.EFFECTS_CHANGED, { sourceId, action: 'register', metadata });

    return true;
  }

  /**
   * Remove all effects from a source
   * @param {string} sourceId - Source identifier to remove
   */
  remove(sourceId) {
    if (!this.effects.has(sourceId)) {
      return false;
    }

    const effects = this.effects.get(sourceId);

    // Remove from stat index
    effects.forEach(effect => {
      const statKey = this._getStatKey(effect);
      if (this.statIndex.has(statKey)) {
        const statEffects = this.statIndex.get(statKey);
        this.statIndex.set(
          statKey,
          statEffects.filter(e => e.source !== sourceId)
        );

        // Clean up empty arrays
        if (this.statIndex.get(statKey).length === 0) {
          this.statIndex.delete(statKey);
        }
      }
    });

    // Remove from effects map
    this.effects.delete(sourceId);

    // [PHASE 2.2 - NOV 15, 2025]: Remove metadata for spell stacking
    this.metadata.delete(sourceId);

    // Track in history
    this.history.push({
      timestamp: Date.now(),
      action: 'remove',
      sourceId
    });
    // [ESR-007 FIX — MAY 2026]: Cap (FIFO) — see register-path comment.
    if (this.history.length > 100) {
      this.history.splice(0, this.history.length - 100);
    }

    // Clear cache
    this.clearCache();

    // Emit event
    systemBus.emit(EVENTS.EFFECTS_CHANGED, { sourceId, action: 'remove' });

    return true;
  }

  /**
   * Remove all effects matching a prefix
   * [INTEGRATION-1 - NOV 15, 2025]: Allow bulk removal by prefix
   * [PURPOSE]: When re-registering items/bonuses, need to clear old ones first
   * [WHY]: EquipmentPanel needs to clear all item: effects before re-registering
   * @param {string} prefix - Prefix to match (e.g., 'item:', 'bonus:')
   * @returns {number} Number of effects removed
   */
  removeByPrefix(prefix) {
    const toRemove = Array.from(this.effects.keys()).filter(key => key.startsWith(prefix));
    let removed = 0;

    toRemove.forEach(key => {
      if (this.remove(key)) {
        removed++;
      }
    });

    if (removed > 0) {
    }

    return removed;
  }

  /**
   * Calculate the effective value of a stat with all effects applied
   * [PHASE 2.2 - NOV 15, 2025]: Updated to pass character to conditional checks
   * PURPOSE: Conditional effects need character data
   *
   * @param {string} stat - Stat name (e.g., 'speed', 'strength', 'ac')
   * @param {number} baseValue - Base value before effects
   * @param {Object} character - Character data for conditional checks
   * @param {Object} context - Context for conditional effects (e.g., {dualWielding: true})
   * @returns {number} Final calculated value
   */
  getEffectiveValue(stat, baseValue, character = null, context = {}) {
    // [ESR-008 FIX — MAY 2026]: Exclude `character` from cache key.
    //
    // Pre-fix: cacheKey = `${stat}_${baseValue}_${JSON.stringify(context)}`
    // The serialized context could include `character: playerStats` (huge:
    // full character object + nested arrays). JSON.stringify ran on every
    // call → non-trivial CPU per render for inventories with 50+ items,
    // and risks throwing on circular references.
    //
    // Safety: character changes are tracked via systemBus events
    // (CHARACTER_LOADED, STATS_CHANGED, EQUIPMENT_CHANGED, FEAT_ADDED/REMOVED)
    // wired to clearCache via _setupEventListeners (ESR-005 fix). So the
    // cache key doesn't need to track character changes — invalidation
    // happens at event-fire time.
    const lightweightContext = {};
    for (const key in context) {
      if (key === 'character') continue; // skip the heavy field
      if (Object.prototype.hasOwnProperty.call(context, key)) {
        lightweightContext[key] = context[key];
      }
    }
    const cacheKey = `${stat}_${baseValue}_${JSON.stringify(lightweightContext)}`;
    const cached = this._getCached(cacheKey);
    if (cached !== null) return cached;

    let value = baseValue;
    // [PHASE 2.2]: Pass character to getEffectsForStat
    const effects = this.getEffectsForStat(stat, character, context);

    // Sort effects by operation priority
    // 1. SET operations (override base)
    // 2. MULTIPLY operations (percentage changes)
    // 3. ADD operations (flat bonuses)
    // 4. SPECIAL operations (advantage, disadvantage, halve, double)
    const sortedEffects = [...effects].sort((a, b) => {
      const priority = { set: 1, multiply: 2, add: 3, halve: 4, double: 5, advantage: 6, disadvantage: 7 };
      return (priority[a.operation] || 99) - (priority[b.operation] || 99);
    });

    // Track highest SET value if multiple exist
    let highestSet = null;
    // [SUB-PLAN 1H-H-cond Phase 1 — 2026-05-29]: Track highest MINIMUM floor value.
    // 'minimum' is a floor operation (value = Math.max(value, minimum)) — semantically
    // distinct from 'set' (which always overrides). E.g. Boots of Striding: walking
    // speed becomes AT LEAST 30ft; a character with 40ft base keeps 40ft.
    let highestMinimum = null;
    // [SUB-PLAN 1H-H-maxcap — 2026-05-29]: Track lowest declared ADD ceiling.
    // Symmetric to highestMinimum. An 'add' effect MAY carry an optional `max` field
    // declaring an upper bound (e.g., Ioun Stones: "+2 DEX to a maximum of 20").
    // After all add operations accumulate we clamp value = Math.min(value, lowestMaximum).
    // Semantics: the TIGHTEST (lowest) ceiling wins when multiple items declare max.
    // lowestMaximum is only updated by 'add' effects that have a numeric `max` field.
    // 'set' / 'minimum' / 'multiply' do NOT contribute — max is only meaningful for adds.
    let lowestMaximum = null;

    sortedEffects.forEach(effect => {
      switch (effect.operation) {
        case 'set':
          // For SET operations, use the highest value
          const setValue = this._getValue(effect.value, false, character);
          if (highestSet === null || setValue > highestSet) {
            highestSet = setValue;
          }
          break;

        case 'minimum':
          // [SUB-PLAN 1H-H-cond Phase 1 — 2026-05-29]: Floor operation.
          // Collect the highest minimum floor; applied AFTER the SET override below
          // so SET wins when SET > minimum (e.g. Belt of Fire Giant Strength 25 > floor 30 → 25).
          const minValue = this._getValue(effect.value, false, character);
          if (highestMinimum === null || minValue > highestMinimum) {
            highestMinimum = minValue;
          }
          break;

        case 'add':
          value += this._getValue(effect.value, false, character);
          // [SUB-PLAN 1H-H-maxcap — 2026-05-29]: Collect declared ceiling from this 'add' effect.
          // effect.max is an optional numeric field; ignore if absent or non-numeric.
          if (typeof effect.max === 'number' && isFinite(effect.max)) {
            if (lowestMaximum === null || effect.max < lowestMaximum) {
              lowestMaximum = effect.max;
            }
          }
          break;

        case 'multiply':
          value *= this._getValue(effect.value, false, character);
          break;

        case 'halve':
          value = Math.floor(value / 2);
          break;

        case 'double':
          value *= 2;
          break;

        // Advantage/disadvantage don't affect the value, they affect rolls
        case 'advantage':
        case 'disadvantage':
          // These are handled separately in roll mechanics
          break;

        default:
          // Unknown operation - log warning for debugging
          console.warn(`[EffectRegistry] Unknown operation: ${effect.operation}`);
          break;
      }
    });

    // [PHASE 2.2 - NOV 15, 2025]: FIX SET operation to always override
    // PURPOSE: D&D 5e compliance - SET effects override all modifiers regardless of value
    // WHY: Belt of Giant Strength (STR=21) should set to 21, even if base+bonuses=22
    // RULE: PHB - "When an effect sets an ability score to a specific value, it overrides all other modifiers"
    // BEFORE BUG: Only applied SET if higher (highestSet > value) ❌
    // AFTER FIX: Always apply SET (D&D 5e rule) ✅
    if (highestSet !== null) {
      value = highestSet; // SET overrides, regardless of value comparison
    }

    // [SUB-PLAN 1H-H-maxcap — 2026-05-29]: Apply ADD ceiling AFTER SET override + all add accumulation.
    // value = Math.min(value, lowestMaximum) — clamps the final accumulated value.
    // Applied after SET so that a SET value is NOT subject to the add-ceiling
    // (a Belt of Giant Strength STR=21 should not be capped by an Ioun Stone max:20).
    // Applied BEFORE the minimum floor so that a minimum cannot push us back above the ceiling
    // (floor = Math.max; ceiling = Math.min; order: ceiling then floor ensures
    // floor wins if floor > ceiling — an invalid game-data edge case that shouldn't occur
    // but is handled defensively: floor then ceiling would silently allow overshoot).
    if (lowestMaximum !== null) {
      value = Math.min(value, lowestMaximum);
    }

    // [SUB-PLAN 1H-H-cond Phase 1 — 2026-05-29]: Apply MINIMUM floor AFTER SET override + ceiling.
    // minimum = Math.max(value, floor) — preserves values already above the floor.
    // Applied after SET so that if both SET and MINIMUM coexist, the minimum
    // can still lift a SET value that fell below the floor (edge case).
    if (highestMinimum !== null) {
      value = Math.max(value, highestMinimum);
    }

    // Cache the result
    this._setCached(cacheKey, value);

    return value;
  }

  /**
   * Calculate the effective value with dice bonuses separated
   * [PHASE 2.2 - NOV 15, 2025]: New method to support dice bonuses (Bless, Guidance, Bardic Inspiration)
   * [HOMEBREW SYSTEM - JAN 10, 2026]: Added numericRollMod and missChance support
   * PURPOSE: Return numeric value AND dice separately for roll mechanics
   * WHY: D&D 5e - Some bonuses are dice (1d4), some are flat (+2), rolls need both
   *
   * @param {string} stat - Stat name (e.g., 'attackRoll', 'savingThrow', 'abilityCheck')
   * @param {number} baseValue - Base numeric value before effects
   * @param {Object} character - Character data for conditional checks
   * @param {Object} context - Context for conditional effects (attackType, ability, etc.)
   * @returns {Object} { value: number, dice: string[], rollMode: string, numericMod: number, missChance: number, effects: Array }
   */
  getEffectiveValueWithDice(stat, baseValue, character = null, context = {}) {
    // Don't cache dice results - they need to be fresh for each roll
    let value = baseValue;
    const dice = [];
    let hasAdvantage = false;
    let hasDisadvantage = false;
    // [HOMEBREW SYSTEM]: Track numeric roll modifiers (stack separately from advantage)
    let numericRollMod = 0;
    // [HOMEBREW SYSTEM]: Track miss chance percentage (concealment)
    let missChance = 0;

    // [PHASE 2.2]: Pass character to getEffectsForStat
    const effects = this.getEffectsForStat(stat, character, context);

    // Sort effects by operation priority
    // [PHASE 2.2]: Added 'addDice' to priority order
    // [HOMEBREW SYSTEM - JAN 10, 2026]: Added numericRollMod and missChance
    const sortedEffects = [...effects].sort((a, b) => {
      const priority = {
        set: 1,
        multiply: 2,
        add: 3,
        addDice: 4, // Dice bonuses after numeric bonuses
        numericRollMod: 5, // [HOMEBREW]: Stacking numeric modifiers
        missChance: 6, // [HOMEBREW]: Concealment percentages
        halve: 7,
        double: 8,
        advantage: 9,
        disadvantage: 10
      };
      return (priority[a.operation] || 99) - (priority[b.operation] || 99);
    });

    // Track highest SET value
    let highestSet = null;
    // [SUB-PLAN 1H-H-maxcap-dice — 2026-05-30]: Mirror lowestMaximum clamp from getEffectiveValue.
    // The add branch in this dice path lacked the ceiling that getEffectiveValue already has.
    // If an 'add' effect carries a numeric `max` field, clamp the accumulated value to the
    // tightest declared ceiling — identical semantics to the proven code in getEffectiveValue.
    let lowestMaximum = null;

    sortedEffects.forEach(effect => {
      switch (effect.operation) {
        case 'set':
          const setValue = this._getValue(effect.value, false, character);
          if (highestSet === null || setValue > highestSet) {
            highestSet = setValue;
          }
          break;

        case 'add':
          value += this._getValue(effect.value, false, character);
          // [SUB-PLAN 1H-H-maxcap-dice — 2026-05-30]: Collect declared ceiling from 'add' effects.
          if (typeof effect.max === 'number' && isFinite(effect.max)) {
            if (lowestMaximum === null || effect.max < lowestMaximum) {
              lowestMaximum = effect.max;
            }
          }
          break;

        case 'multiply':
          value *= this._getValue(effect.value, false, character);
          break;

        case 'addDice':
          // [PHASE 2.2]: Add dice to array (preserving string format)
          const diceValue = this._getValue(effect.value, true, character); // true = preserve dice string
          if (diceValue && !dice.includes(diceValue)) {
            dice.push(diceValue); // '1d4', '1d6', etc.
          }
          break;

        case 'halve':
          value = Math.floor(value / 2);
          break;

        case 'double':
          value *= 2;
          break;

        case 'advantage':
          hasAdvantage = true;
          break;

        case 'disadvantage':
          hasDisadvantage = true;
          break;

        // [HOMEBREW SYSTEM - JAN 10, 2026]: Numeric roll modifiers
        // WHY: -4 penalty is NOT disadvantage - it stacks additively
        // EXAMPLE: -4 (Gloom Strike) + advantage (Invisible) = roll twice, take higher, -4
        case 'numericRollMod':
          numericRollMod += this._getValue(effect.value, false, character);
          break;

        // [HOMEBREW SYSTEM - JAN 10, 2026]: Miss chance (concealment)
        // WHY: 20% concealment means roll d100 after hit, miss on 1-20
        case 'missChance':
          missChance += this._getValue(effect.value, false, character);
          // Cap at 100%
          if (missChance > 100) missChance = 100;
          break;

        default:
          console.warn(`[EffectRegistry] Unknown operation: ${effect.operation}`);
          break;
      }
    });

    // Apply SET value ALWAYS (not just when higher) - D&D 5e rule
    if (highestSet !== null) {
      value = highestSet;
    }

    // [SUB-PLAN 1H-H-maxcap-dice — 2026-05-30]: Apply ADD ceiling after SET override + all adds.
    // Mirrors getEffectiveValue's lowestMaximum clamp. SET values are NOT subject to the
    // add-ceiling (a Belt of Giant Strength STR=21 should not be capped by max:20 add effect).
    if (lowestMaximum !== null) {
      value = Math.min(value, lowestMaximum);
    }

    // [PHASE 2.2]: D&D 5e Rule - Advantage and disadvantage cancel each other
    // PHB p.173: "If circumstances cause a roll to have both advantage and disadvantage,
    // you are considered to have neither of them, and you roll one d20."
    const rollMode = hasAdvantage && hasDisadvantage ? 'normal'
                   : hasAdvantage ? 'advantage'
                   : hasDisadvantage ? 'disadvantage'
                   : 'normal';

    return {
      value,        // Numeric total (flat bonuses applied)
      dice,         // Array of dice strings ['1d4', '1d6'] for rolling
      rollMode,     // 'normal', 'advantage', or 'disadvantage'
      // [HOMEBREW SYSTEM - JAN 10, 2026]: New return values
      numericRollMod, // Flat +/- that stacks separately from advantage
      missChance,     // Concealment percentage (0-100)
      effects       // All effects that applied (for breakdown/debugging)
    };
  }

  /**
   * Get all effects affecting a specific stat
   * [PHASE 2.2 - NOV 15, 2025]: Updated to pass character to conditional checks
   * PURPOSE: Conditional effects need character data (classes, level, equipment)
   * WHY: Effects like "when wearing heavy armor" or "when raging" need character state
   *
   * @param {string} stat - Stat name
   * @param {Object} character - Character data for conditional checks
   * @param {Object} context - Context for conditional effects
   * @returns {Array} Array of effects affecting this stat
   */
  getEffectsForStat(stat, character = null, context = {}) {
    const statKey = stat.toLowerCase();
    let effects = [...(this.statIndex.get(statKey) || [])]; // Copy array to allow modifications

    // [PHASE 2.3 - NOV 15, 2025]: Generate virtual effects for calculated stats
    // [PURPOSE]: Stats like Passive Perception are calculated from other stats
    // [WHY]: Breakdown tooltips need to show all components (WIS mod, proficiency, feats)
    effects = this._generateCalculatedStatEffects(statKey, character, context, effects);

    // Filter by conditions
    return effects.filter(effect => {
      if (!effect.condition || effect.condition === 'always') {
        return true;
      }

      // Check conditional effects
      if (typeof effect.condition === 'object' && effect.condition.when) {
        // [PHASE 2.2]: Pass BOTH character and context to condition check
        if (effect.condition.check && typeof effect.condition.check === 'function') {
          try {
            return effect.condition.check(character, context);
          } catch (error) {
            console.warn('[EffectRegistry] Condition check failed:', error);
            return false;
          }
        }

        // Simple string condition check
        return context[effect.condition.when] === true;
      }

      // [Q9 — 2026-05-16]: Bare-string condition matching ('class:<name>' etc.).
      // Pre-fix, any string condition that wasn't 'always' silently fell through to
      // `return true`, meaning 9 magical items in BASE_ITEMS.magical_items (Rod of the
      // Pact Keeper +1/+2, Staff of the Woodlands, Amulet of the Devout +1, Arcane
      // Grimoire +1, Bloodwell Vial +1, Devotee's Censer +1, Rhythm-Maker's Drum +1,
      // Moon Sickle +1) applied their +N spell attack / spell DC bonuses to every
      // character regardless of class — a Fighter could attune (well, not really,
      // because requiresAttunement gates separately) but more importantly the
      // multiclass scenario where a Wizard/Fighter holds an Arcane Grimoire and gets
      // +1 from the Wizard side was indistinguishable from a pure Fighter getting
      // the same +1. The data-driven parse below handles `class:<name>` and the
      // comma-separated `class:<a>,<b>` form. Defensive fail-safe: missing character
      // or malformed condition strings drop the effect (safer than misapplying).
      // [SUB-PLAN 1H-H-cond Phase 1 — 2026-05-29]: Generic structured leaf evaluator.
      // Detected by `cond.category` presence (distinct from `cond.when` used by the
      // object-with-when branch above). MUST be checked BEFORE the bare-string branch.
      // [SUB-PLAN 1H-H-cond-combinators — 2026-05-29]: AND/OR/NOT combinator nodes.
      // Detected by `all`/`any`/`none` key presence. Routes through the same
      // _evaluateConditionBlock method which now handles both LEAF and COMBINATOR shapes.
      if (typeof effect.condition === 'object' && effect.condition !== null &&
          (effect.condition.category || 'all' in effect.condition || 'any' in effect.condition || 'none' in effect.condition)) {
        return this._evaluateConditionBlock(effect.condition, character, context);
      }

      if (typeof effect.condition === 'string') {
        return this._matchesStringCondition(effect.condition, character);
      }

      return true;
    });
  }

  /**
   * [SUB-PLAN 1H-H-cond Phase 1 — 2026-05-29]: Evaluate a structured condition node.
   *
   * Handles two shapes:
   *
   * LEAF (Phase 1): { category, op, operands[] }
   *   - category: dimension tested (e.g. 'equippedWeaponType'); dispatched to _resolveCategory
   *   - op: 'hasAny' | 'hasAll' | 'is' | 'isNot'; unknown op → true (forward-compat)
   *   - operands: catalog-typed ids (NEVER hardcoded strings; caller is responsible for
   *     matching what the resolver returns)
   *
   * COMBINATOR (Sub-plan 1H-H-cond-combinators — 2026-05-29): { all | any | none }
   *   - { all: [node,...] }  — AND: every child must evaluate to true.
   *                            Empty array → true (vacuous truth: "all of nothing" = true).
   *   - { any: [node,...] }  — OR:  at least one child must evaluate to true.
   *                            Empty array → false ("any of nothing" = false).
   *   - { none: [node,...] } — NOR: no child may evaluate to true.
   *                            Empty array → true (vacuous: "none of nothing" = true).
   *   Children can be LEAF nodes or nested COMBINATOR nodes (arbitrary depth).
   *   Backward-compat: a bare LEAF shape is simply a leaf at any depth.
   *
   * BREADCRUMB: `none`/NOT-in-UI and arbitrary-depth combinator authoring UI are deferred.
   *   The evaluator supports them; the UI (ConditionKindSlot) currently authors a
   *   1-level all/any combine only. Master-queue 1H-H-cond-combinators UI arc.
   *
   * @param {Object} cond      - LEAF or COMBINATOR node
   * @param {Object} character - Character data
   * @param {Object} context   - Runtime context (from _buildContext)
   * @returns {boolean}
   */
  _evaluateConditionBlock(cond, character, context) {
    if (!cond || typeof cond !== 'object') return true;

    // ── COMBINATOR branch ──────────────────────────────────────────────────────
    // Detected by presence of `all`, `any`, or `none` key.
    // Children are recursed through the same method (supports arbitrary depth).

    if ('all' in cond) {
      // AND — every child must be true. Empty array → true (vacuous).
      const children = Array.isArray(cond.all) ? cond.all : [];
      return children.every(child => this._evaluateConditionBlock(child, character, context));
    }

    if ('any' in cond) {
      // OR — at least one child must be true. Empty array → false.
      const children = Array.isArray(cond.any) ? cond.any : [];
      return children.some(child => this._evaluateConditionBlock(child, character, context));
    }

    if ('none' in cond) {
      // NOR — no child may be true. Empty array → true (vacuous).
      const children = Array.isArray(cond.none) ? cond.none : [];
      return !children.some(child => this._evaluateConditionBlock(child, character, context));
    }

    // ── LEAF branch ────────────────────────────────────────────────────────────
    // Reached when the node has `category` (no combinator key present).

    const actual = this._resolveCategory(cond.category, character, context);
    const operands = Array.isArray(cond.operands) ? cond.operands : [];

    switch (cond.op) {
      case 'hasAny':
        return operands.some(o => actual.has(o));
      case 'hasAll':
        return operands.every(o => actual.has(o));
      case 'is':
        return operands.length > 0 && actual.has(operands[0]);
      case 'isNot':
        return operands.length === 0 || !actual.has(operands[0]);
      default:
        // Unknown op → forward-compat pass
        return true;
    }
  }

  /**
   * [SUB-PLAN 1H-H-cond Phase 1 — 2026-05-29]: Resolve a category name to a Set of
   * current values from context. Returns empty Set for unknown categories (→ won't match).
   *
   * Phase 1 categories:
   *   'equippedWeaponType' → Set containing context.equippedWeaponBaseType (if non-null)
   *
   * @param {string} category
   * @param {Object} character
   * @param {Object} context
   * @returns {Set<string>}
   */
  _resolveCategory(category, character, context) {
    switch (category) {
      case 'equippedWeaponType': {
        const baseType = context && context.equippedWeaponBaseType;
        return baseType ? new Set([baseType]) : new Set();
      }
      // [SUB-PLAN 1H-H-cond Phase 2 — GAP-8 — 2026-05-29]: Save-source dimension.
      // Used by staff_of_the_magi (+2 to saves vs spells) and any future "save vs X"
      // effect. context.rollSourceType is set by the save-roll caller (defaults undefined
      // = mundane; 'vs_spells' when the save is triggered by a spell).
      // Returns a single-element Set so hasAny(['vs_spells']) evaluates correctly.
      case 'rollSourceType': {
        const srcType = context && context.rollSourceType;
        return srcType ? new Set([srcType]) : new Set();
      }
      default:
        // Unknown category → empty Set → no operand can match → effect doesn't apply.
        // Forward-compat: categories added in later phases just need a new case here.
        return new Set();
    }
  }

  /**
   * Match a bare-string condition (e.g., 'class:wizard', 'class:cleric,paladin')
   * against the supplied character.
   *
   * Supported prefixes:
   *   - 'class:<name>'           — character has any level in <name> (case-insensitive)
   *   - 'class:<a>,<b>,...'      — character has any level in ANY listed class
   *
   * Unknown prefixes return `true` to preserve forward-compat (future content may
   * use new prefixes that older code shouldn't suppress); malformed `class:` strings
   * with no parsable className return `false` (fail-safe).
   *
   * Data-driven per Rule 11: no hardcoded class list inside this helper. Class
   * names are extracted from the condition string at runtime and compared against
   * `character.classes[].className` case-insensitively, matching the precedent at
   * the multiplier-formula resolver (L1307-L1309).
   *
   * Multiclass semantics: logical OR — any class entry on the character that
   * matches any name in the condition list returns true.
   *
   * @param {string} conditionString - Bare-string condition (e.g., 'class:wizard')
   * @param {Object} character - Character data (must have `.classes` array)
   * @returns {boolean} True if the effect should apply
   */
  _matchesStringCondition(conditionString, character) {
    if (typeof conditionString !== 'string' || !conditionString.includes(':')) {
      // Not a structured condition — preserve pre-fix accept behavior
      return true;
    }

    const colonIdx = conditionString.indexOf(':');
    const prefix = conditionString.slice(0, colonIdx).trim().toLowerCase();
    const payload = conditionString.slice(colonIdx + 1).trim();

    if (prefix === 'class') {
      // Parse comma-separated class list (e.g., 'cleric,paladin' or 'wizard')
      const allowedClasses = payload
        .split(',')
        .map(name => name.trim().toLowerCase())
        .filter(name => name.length > 0);

      // Malformed — no parsable class name → drop the effect (fail-safe)
      if (allowedClasses.length === 0) {
        return false;
      }

      // Missing character or classes array → drop the effect (fail-safe)
      if (!character || !Array.isArray(character.classes) || character.classes.length === 0) {
        return false;
      }

      // Logical OR: any character class matching any allowed class wins
      return character.classes.some(classEntry => {
        const characterClassName = String(classEntry?.className || '').toLowerCase();
        return characterClassName.length > 0 && allowedClasses.includes(characterClassName);
      });
    }

    // Unknown prefix — preserve pre-fix accept behavior (forward-compat for
    // hypothetical future prefixes like 'subclass:', 'race:', 'feat:'). A future
    // sweep should add explicit handlers for any new prefix it introduces.
    return true;
  }

  /**
   * Get a breakdown of all effects affecting a stat
   * [PHASE 2.2 - NOV 15, 2025]: Updated to pass character to conditional checks
   * PURPOSE: Conditional effects need character data
   *
   * @param {string} stat - Stat name
   * @param {number} baseValue - Base value before effects
   * @param {Object} character - Character data for conditional checks
   * @param {Object} context - Context for conditional effects
   * @returns {Object} Breakdown of effects by source
   */
  /**
   * Returns the proficiency level (0=none, 1=proficient, 2=expertise) granted by
   * registered effects for a given proficiency key.
   *
   * @param {string} profKey - Either a fully-qualified key ('skill:perception',
   *   'save:wisdom', 'tool:smiths_tools', 'armor:light', 'weapon:martial')
   *   OR a bare skill name for backward compat ('perception' → 'skill:perception').
   * @param {Object|null} character
   * @returns {number} 0 = none, 1 = proficient, 2 = expertise
   *
   * [Phase 4a]: Allows custom feats and invocations to grant skill proficiency.
   * [Phase 4b bucket 2 — APR 2026]: Generalized to handle saves, tools, armor,
   *   weapon proficiency in addition to skills. Backward compat preserved for
   *   callers that pass bare skill keys (no colon) — they're auto-prefixed
   *   with 'skill:'. Match also accepts 'category:all' for category-wide grants
   *   (e.g., Monk Diamond Soul registers 'save:all' for proficiency in all saves).
   */
  getGrantedProficiencyLevel(profKey, character = null) {
    if (!profKey) return 0;
    // Backward compat: bare key (no colon) is a skill key
    const fullKey = profKey.includes(':') ? profKey : `skill:${profKey}`;
    const [category] = fullKey.split(':');
    const categoryAll = `${category}:all`;

    const effects = this.getEffectsForStat('proficiency', character, {});
    let level = 0;
    effects.forEach(effect => {
      if (effect.operation === 'grant' &&
          (effect.value === fullKey || effect.value === categoryAll)) {
        const granted = (effect.proficiencyLevel === 2) ? 2 : 1;
        if (granted > level) level = granted;
      }
    });
    return level;
  }

  getBreakdown(stat, baseValue = 0, character = null, context = {}) {
    // [PHASE 2.2]: Pass character to getEffectsForStat and getEffectiveValue
    const effects = this.getEffectsForStat(stat, character, context);
    const breakdown = {
      base: baseValue,
      effects: [],
      total: this.getEffectiveValue(stat, baseValue, character, context)
    };

    // [NS-AC-source-label-name 2026-05-31]: inventory lets item sources resolve
    // to the item's real display name instead of the opaque/title-cased id.
    const { inventory = [] } = context;

    // Group effects by source for cleaner display
    const bySource = new Map();
    effects.forEach(effect => {
      if (!bySource.has(effect.source)) {
        bySource.set(effect.source, []);
      }
      bySource.get(effect.source).push({
        operation: effect.operation,
        value: this._getValue(effect.value, false, character),
        condition: effect.condition
      });
    });

    // Format for display
    bySource.forEach((effectList, source) => {
      // Parse source ID for readable name
      const [type, id] = source.split(':');
      const displayName = this._getSourceDisplayName(type, id, inventory);

      effectList.forEach(effect => {
        let modifier = '';
        switch (effect.operation) {
          case 'add':
            modifier = effect.value >= 0 ? `+${effect.value}` : `${effect.value}`;
            break;
          case 'set':
            modifier = `=${effect.value}`;
            break;
          case 'multiply':
            modifier = `×${effect.value}`;
            break;
          case 'halve':
            modifier = '÷2';
            break;
          case 'double':
            modifier = '×2';
            break;
          // [HOMEBREW SYSTEM - JAN 10, 2026]: Format homebrew operations
          case 'numericRollMod':
            modifier = effect.value >= 0 ? `+${effect.value} (flat)` : `${effect.value} (flat)`;
            break;
          case 'missChance':
            modifier = `${effect.value}% miss`;
            break;
          default:
            modifier = effect.operation;
        }

        breakdown.effects.push({
          source: displayName,
          modifier,
          value: effect.value,
          operation: effect.operation,
          conditional: effect.condition && effect.condition !== 'always'
        });
      });
    });

    return breakdown;
  }

  /**
   * [PHASE 2.3 - NOV 15, 2025]: Get breakdown with visual formatting for tooltips
   * [PURPOSE]: Enhanced version of getBreakdown() with color/icon metadata for UI display
   * [WHY]: Players need beautiful, informative tooltips showing all effect sources
   * [INTEGRATION]: Used by EffectBreakdownTooltip component
   *
   * @param {string} stat - Stat type ('ac', 'initiative', 'speed', etc.)
   * @param {number} baseValue - Base value before effects
   * @param {object} character - Character object
   * @param {object} context - Context object (inventory, activeQuickBonuses, etc.)
   * @returns {object} Formatted breakdown with visual metadata
   *
   * @example
   * effectRegistry.getBreakdownForDisplay('ac', 10, character, { inventory, activeQuickBonuses });
   * // Returns:
   * {
   *   stat: 'ac',
   *   base: { value: 10, label: 'Base AC', color: 'text-gray-300' },
   *   effects: [
   *     {
   *       source: 'DEX Modifier',
   *       sourceType: 'ability',
   *       modifier: '+3',
   *       value: 3,
   *       operation: 'add',
   *       color: 'text-blue-400',
   *       icon: '⚡',
   *       conditional: false
   *     },
   *     {
   *       source: 'Shield of Faith',
   *       sourceType: 'spell',
   *       modifier: '+2',
   *       value: 2,
   *       operation: 'add',
   *       color: 'text-purple-400',
   *       icon: '✨',
   *       conditional: false,
   *       metadata: { spellLevel: 1, casterName: 'Self' }
   *     }
   *   ],
   *   total: 15,
   *   formula: '10 + 3 + 2 = 15',
   *   hasConditionals: false,
   *   hasDice: false,
   *   stackingPrevented: []
   * }
   */
  getBreakdownForDisplay(stat, baseValue = 0, character = null, context = {}) {
    // [STEP 1]: Get standard breakdown
    const breakdown = this.getBreakdown(stat, baseValue, character, context);

    // [STEP 2]: Build formatted result
    const formatted = {
      stat,
      base: {
        value: breakdown.base,
        label: this._getBaseLabel(stat),
        color: 'text-gray-300'
      },
      effects: [],
      total: breakdown.total,
      formula: '',
      hasConditionals: false,
      hasDice: false,
      stackingPrevented: []
    };

    // [STEP 3]: Format each effect with visual metadata
    breakdown.effects.forEach(effect => {
      const category = this._categorizeSourceType(effect.source);
      const isDice = typeof effect.value === 'string' && /^\d+d\d+/.test(effect.value);

      const formattedEffect = {
        // [NS-AC-source-label-name 2026-05-31]: effect.source is ALREADY the
        // resolved display name from getBreakdown() (incl. item.name); the prior
        // re-call here was a no-op pass-through (single-arg → !id guard).
        source: effect.source,
        sourceType: category.type,
        modifier: effect.modifier,
        value: effect.value,
        operation: effect.operation,
        color: category.color,
        icon: category.icon,
        conditional: effect.conditional || false,
        isDice
      };

      // Add metadata if present (for spells)
      const sourceId = effect.source;
      if (this.metadata.has(sourceId)) {
        formattedEffect.metadata = this.metadata.get(sourceId);
      }

      formatted.effects.push(formattedEffect);

      // Track flags
      if (effect.conditional) formatted.hasConditionals = true;
      if (isDice) formatted.hasDice = true;
    });

    // [STEP 4]: Build formula string
    formatted.formula = this._buildFormula(formatted);

    // [STEP 5]: Check for stacking prevention
    // TODO: Add stacking prevention tracking when effects are rejected
    formatted.stackingPrevented = [];

    return formatted;
  }

  /**
   * [PHASE 2.3 - NOV 15, 2025]: Helper - Get base label for stat
   * [PURPOSE]: Convert stat names to user-friendly labels
   */
  _getBaseLabel(stat) {
    const labels = {
      'ac': 'Base AC',
      'initiative': 'Base Initiative',
      'speed': 'Base Speed',
      'passivePerception': 'Base (10)',
      'proficiencyBonus': 'Base Proficiency',
      'spellAttackBonus': 'Base',
      'spellSaveDC': 'Base (8)',
      'attackRoll': 'Base',
      'damageRoll': 'Base Damage',
      'savingThrow': 'Base Save',
      'abilityCheck': 'Base Check',
      'strength': 'Base STR',
      'dexterity': 'Base DEX',
      'constitution': 'Base CON',
      'intelligence': 'Base INT',
      'wisdom': 'Base WIS',
      'charisma': 'Base CHA'
    };
    return labels[stat] || 'Base Value';
  }

  /**
   * [PHASE 2.3 - NOV 15, 2025]: Helper - Categorize source type for visual styling
   * [PURPOSE]: Assign colors and icons based on effect source
   * [WHY]: Visual categorization helps players understand effect origins
   */
  _categorizeSourceType(source) {
    const [type, id] = source.split(':');

    // Category metadata: type, color, icon
    const categories = {
      'ability': { type: 'ability', color: 'text-blue-400', icon: '⚡' },
      'item': { type: 'item', color: 'text-green-400', icon: '🎒' },
      'feat': { type: 'feat', color: 'text-amber-400', icon: '⭐' },
      'buff': { type: 'spell', color: 'text-purple-400', icon: '✨' },  // Spells/buffs
      'race': { type: 'race', color: 'text-cyan-400', icon: '🧬' },
      'class': { type: 'class', color: 'text-orange-400', icon: '🎓' },
      'proficiency': { type: 'proficiency', color: 'text-amber-400', icon: '📚' },
      'level': { type: 'level', color: 'text-yellow-400', icon: '⬆️' },
      'base': { type: 'base', color: 'text-gray-400', icon: '📋' },
      'custom': { type: 'custom', color: 'text-pink-400', icon: '✏️' }
    };

    return categories[type] || { type: 'other', color: 'text-gray-400', icon: '📋' };
  }

  /**
   * [PHASE 2.3 - NOV 15, 2025]: Helper - Build formula string
   * [PURPOSE]: Create human-readable formula (e.g., "10 + 3 + 2 = 15")
   * [WHY]: Shows players exactly how stat is calculated
   */
  _buildFormula(formatted) {
    let formula = `${formatted.base.value}`;
    let diceTerms = [];

    formatted.effects.forEach(effect => {
      if (effect.operation === 'set') {
        // SET operations override everything
        formula = `${effect.value} (SET)`;
        return;
      }

      if (effect.isDice) {
        diceTerms.push(effect.value);
      } else {
        switch (effect.operation) {
          case 'add':
            formula += ` + ${Math.abs(effect.value)}`;
            break;
          case 'multiply':
            formula += ` × ${effect.value}`;
            break;
          case 'halve':
            formula += ' ÷ 2';
            break;
          case 'double':
            formula += ' × 2';
            break;
          default:
            break;
        }
      }
    });

    // Add total
    formula += ` = ${formatted.total}`;

    // Add dice terms
    if (diceTerms.length > 0) {
      formula += ` + ${diceTerms.join(' + ')}`;
    }

    return formula;
  }

  /**
   * [PHASE 2.3 - NOV 15, 2025]: Generate virtual effects for calculated stats
   * [PURPOSE]: Stats like Passive Perception, Proficiency Bonus, Spell Attack/Save DC are calculated
   * [WHY]: Breakdown tooltips need to show component parts (ability mods, level-based values, etc.)
   *
   * These stats are NOT registered as effects - they're calculated from character data.
   * This method dynamically generates effect objects to populate the breakdown.
   *
   * @param {string} stat - Stat key ('passiveperception', 'proficiencybonus', etc.)
   * @param {object} character - Character object
   * @param {object} context - Context (inventory, activeQuickBonuses)
   * @param {array} effects - Existing registered effects
   * @returns {array} Effects array with generated virtual effects added
   */
  _generateCalculatedStatEffects(stat, character, context, effects) {
    if (!character) return effects; // Can't calculate without character data

    const { inventory = [], activeQuickBonuses = [] } = context;

    // Passive Perception = 10 + WIS mod + (prof if proficient) + (Observant +5)
    if (stat === 'passiveperception') {
      // WIS modifier
      // [FIX NOV 17, 2025]: Calculate modifier directly - this.getEffectiveModifier doesn't exist
      const wisMod = Math.floor(((character.wisdom || 10) - 10) / 2);
      if (wisMod !== 0) {
        effects.push({
          source: 'ability:wisdom',
          type: 'passivePerception',
          operation: 'add',
          value: wisMod,
          condition: 'always'
        });
      }

      // Proficiency bonus (if proficient in Perception)
      const isProficient = character.proficiencies?.skills?.includes('perception');
      if (isProficient) {
        const profBonus = Math.floor(((character.level || 1) - 1) / 4) + 2;
        effects.push({
          source: 'proficiency:perception',
          type: 'passivePerception',
          operation: 'add',
          value: profBonus,
          condition: 'always'
        });
      }

      // Observant feat (+5)
      const hasObservant = character.feats?.some(f =>
        (typeof f === 'string' && f.toLowerCase() === 'observant') ||
        (typeof f === 'object' && f.name?.toLowerCase() === 'observant')
      );
      if (hasObservant) {
        effects.push({
          source: 'feat:Observant',
          type: 'passivePerception',
          operation: 'add',
          value: 5,
          condition: 'always'
        });
      }
    }

    // Proficiency Bonus = Math.ceil(level / 4) + 1
    if (stat === 'proficiencybonus') {
      // [FIX NOV 17, 2025]: Use character.level directly (was calling non-existent _getTotalLevel)
      const level = character.level || 1;
      const baseProfBonus = Math.ceil(level / 4) + 1;

      effects.push({
        source: 'level:base',
        type: 'proficiencyBonus',
        operation: 'add',
        value: baseProfBonus,
        condition: 'always'
      });

      // Future: Stone of Good Luck adds +1 to proficiency bonus
      // Check inventory for such items when implemented
    }

    // Spell Attack Bonus = proficiency + ability modifier + item bonuses
    if (stat === 'spellattackbonus') {
      const profBonus = Math.floor(((character.level || 1) - 1) / 4) + 2;
      const spellAbility = character.spellcastingAbility || 'intelligence';
      // [FIX NOV 17, 2025]: Calculate modifier directly - this.getEffectiveModifier doesn't exist
      const abilityMod = Math.floor(((character[spellAbility] || 10) - 10) / 2);

      effects.push({
        source: 'proficiency:spellcasting',
        type: 'spellAttackBonus',
        operation: 'add',
        value: profBonus,
        condition: 'always'
      });

      effects.push({
        source: `ability:${spellAbility}`,
        type: 'spellAttackBonus',
        operation: 'add',
        value: abilityMod,
        condition: 'always'
      });

      // Custom spell attack bonuses (Wand of the War Mage, etc.)
      if (character.customSpellAttackBonuses) {
        character.customSpellAttackBonuses.forEach(bonus => {
          effects.push({
            source: `custom:${bonus.name}`,
            type: 'spellAttackBonus',
            operation: 'add',
            value: bonus.value,
            condition: 'always'
          });
        });
      }
    }

    // Spell Save DC = 8 + proficiency + ability modifier + item bonuses
    if (stat === 'spellsavedc') {
      const profBonus = Math.floor(((character.level || 1) - 1) / 4) + 2;
      const spellAbility = character.spellcastingAbility || 'intelligence';
      // [FIX NOV 17, 2025]: Calculate modifier directly - this.getEffectiveModifier doesn't exist
      const abilityMod = Math.floor(((character[spellAbility] || 10) - 10) / 2);

      effects.push({
        source: 'base:spellDC',
        type: 'spellSaveDC',
        operation: 'add',
        value: 8,
        condition: 'always'
      });

      effects.push({
        source: 'proficiency:spellcasting',
        type: 'spellSaveDC',
        operation: 'add',
        value: profBonus,
        condition: 'always'
      });

      effects.push({
        source: `ability:${spellAbility}`,
        type: 'spellSaveDC',
        operation: 'add',
        value: abilityMod,
        condition: 'always'
      });

      // Custom spell save bonuses (Rod of the Pact Keeper, etc.)
      if (character.customSpellSaveBonuses) {
        character.customSpellSaveBonuses.forEach(bonus => {
          effects.push({
            source: `custom:${bonus.name}`,
            type: 'spellSaveDC',
            operation: 'add',
            value: bonus.value,
            condition: 'always'
          });
        });
      }
    }

    return effects;
  }

  /**
   * Clear all effects (used for character reset)
   * [PHASE 2.2 - NOV 15, 2025]: Also clears metadata
   */
  clear() {
    this.effects.clear();
    this.statIndex.clear();
    this.metadata.clear(); // [PHASE 2.2]: Clear spell stacking metadata
    this.clearCache();
    this.history = [];
    // [FIX APR 2026]: Uncommented — EFFECTS_CHANGED DOES exist (systemBus.js:298)
    // Previous comment was incorrect. Clearing registry must notify listeners.
    systemBus.emit(EVENTS.EFFECTS_CHANGED, { action: 'clear' });
  }

  /**
   * Clear the calculation cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get all active effects (for debugging)
   */
  getAllEffects() {
    const allEffects = [];
    this.effects.forEach((effectList, sourceId) => {
      effectList.forEach(effect => {
        allEffects.push({ source: sourceId, ...effect });
      });
    });
    return allEffects;
  }

  /**
   * Convert legacy effect formats to unified schema
   */
  static convertLegacyEffect(legacyEffect, source = 'unknown') {
    const effects = [];

    // Handle feat passiveEffects object format
    if (source.startsWith('feat:') && typeof legacyEffect === 'object' && !Array.isArray(legacyEffect)) {
      Object.entries(legacyEffect).forEach(([key, value]) => {
        if (key === 'initiative' && typeof value === 'number') {
          effects.push({ type: 'initiative', operation: 'add', value });
        } else if ((key === 'speed' || key === 'speedBonus') && typeof value === 'number') {
          effects.push({ type: 'speed', operation: 'add', value });
        } else if (key === 'hpPerLevel' && typeof value === 'number') {
          effects.push({ type: 'hp', operation: 'add', value: `${value}*level` });
        } else if (key === 'bonusHP' && typeof value === 'number') {
          effects.push({ type: 'hp', operation: 'add', value });
        } else if (key === 'acBonus' && typeof value === 'number') {
          effects.push({ type: 'ac', operation: 'add', value });
        } else if (key === 'acBonusWhileDualWielding' && typeof value === 'number') {
          effects.push({
            type: 'ac',
            operation: 'add',
            value,
            condition: { when: 'dualWielding' }
          });
        } else if (key === 'passivePerception' && typeof value === 'number') {
          effects.push({ type: 'skill', target: 'perception', operation: 'add', value });
        } else if (key === 'passiveInvestigation' && typeof value === 'number') {
          effects.push({ type: 'skill', target: 'investigation', operation: 'add', value });
        } else if (key === 'carryCapacityMultiplier' && typeof value === 'number') {
          effects.push({ type: 'carryCapacity', operation: 'multiply', value });
        }
        // Ability score bonuses
        else if (['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'].includes(key)) {
          effects.push({ type: 'ability', target: key.substring(0, 3), operation: 'add', value });
        }
      });
    }

    // Handle item statModifications format
    else if (source.startsWith('item:') && typeof legacyEffect === 'object') {
      Object.entries(legacyEffect).forEach(([key, value]) => {
        // Parse keys like "strengthSet", "dexterityAdd"
        const match = key.match(/^(strength|dexterity|constitution|intelligence|wisdom|charisma)(Set|Add)$/i);
        if (match) {
          const ability = match[1].substring(0, 3).toLowerCase();
          const operation = match[2].toLowerCase() === 'set' ? 'set' : 'add';
          effects.push({ type: 'ability', target: ability, operation, value });
        } else if (key === 'ac' || key === 'acBonus') {
          effects.push({ type: 'ac', operation: 'add', value });
        } else if (key === 'speed' || key === 'speedBonus') {
          effects.push({ type: 'speed', operation: 'add', value });
        } else if (key === 'maxHP' || key === 'bonusHP') {
          effects.push({ type: 'hp', operation: 'add', value });
        }
      });
    }

    // Handle already-normalized array format
    else if (Array.isArray(legacyEffect)) {
      return legacyEffect; // Already in correct format
    }

    return effects;
  }

  // === Private Methods ===

  _normalizeEffect(effect, sourceId) {
    // Ensure required fields
    if (!effect.type) {
      console.warn(`[EffectRegistry] Effect missing type from ${sourceId}:`, effect);
      effect.type = 'unknown';
    }

    // Default operation is 'add'
    if (!effect.operation) {
      effect.operation = 'add';
    }

    // Normalize target for ability/skill/save effects
    if (effect.type === 'ability' && effect.target) {
      // Convert full names to 3-letter codes
      const abilityMap = {
        strength: 'str', dexterity: 'dex', constitution: 'con',
        intelligence: 'int', wisdom: 'wis', charisma: 'cha'
      };
      effect.target = abilityMap[effect.target.toLowerCase()] || effect.target.substring(0, 3).toLowerCase();
    }

    return effect;
  }

  _getStatKey(effect) {
    // Create a consistent key for stat indexing
    // [TASK 1 - APR 2026]: Normalize to lowercase so _getStatKey matches getEffectsForStat's lowercase lookup
    if (effect.type === 'ability' && effect.target) {
      return effect.target; // str, dex, con, etc. (already lowercase by _normalizeEffect)
    }
    if (effect.type === 'skill' && effect.target) {
      return `skill:${effect.target}`;
    }
    if (effect.type === 'save' && effect.target) {
      return `save:${effect.target}`;
    }
    // [Sub-plan 1Q-F-7b-ii 2026-05-28]: Route type:'check' to indexed key 'check:<target>'.
    // Mirrors the save branch above. Enables hasAutoFailCheck + hasDisadvantageOnCheck
    // to query ability-specific and 'check:all' scopes via getEffectsForStat.
    if (effect.type === 'check' && effect.target) {
      return `check:${effect.target}`;
    }
    return (effect.type || '').toLowerCase(); // ac, speed, hp, initiative, damageBonus → damagebonus, etc.
  }

  /**
   * Get the value from an effect, handling numbers, strings, dice notation, and formula strings
   * [PHASE 2.2 - NOV 15, 2025]: Added dice string support for Bless, Guidance, Bardic Inspiration
   * [TASK 1 - APR 2026]: Added ability modifier and proficiency_bonus formula resolution
   * PURPOSE: Support both numeric bonuses (+2), dice bonuses (1d4), and formula strings ('cha_modifier')
   * WHY: D&D 5e has both types - some effects add flat numbers, others add dice or dynamic values
   *
   * @param {any} value - Effect value (number, string, dice notation, or formula string)
   * @param {boolean} preserveDice - If true, return dice strings as-is (for dice bonuses)
   * @param {Object|null} character - Character data for resolving formula strings
   * @returns {number|string} Numeric value or dice string
   */
  /**
   * [SUB-PLAN 3D-S2 — 2026-05-28]: Resolve a structured scaling token object
   * to a base numeric value. Called from the object-value branch in _getValue.
   *
   * Supported tokens:
   *   characterLevel     — total character level (character.level)
   *   classLevel         — specific class level (target = className)
   *   proficiencyBonus   — Math.ceil(level/4) + 1  (same as legacy 'proficiency_bonus' string)
   *   proficiencyTier    — 1-4 bracket (levels 1-4 = 1, 5-8 = 2, 9-12 = 3, 13+ = 4)
   *   spellSlotLevelExpended — spell slot tier used at cast-time; defaults to 0 (Combat Tracker dependency)
   *   resourceSpent      — number of a named resource pool spent; defaults to 0 (Combat Tracker dep)
   *   str/dex/con/int/wis/cha modifiers — existing ability mod calc reused via _getValue recursion
   *
   * @param {string} token
   * @param {string|null} target   parametric target (className or resourcePool key)
   * @param {Object|null} character
   * @param {Object|null} context  optional combat context {spellSlotLevel, resourceSpent}
   * @returns {number}
   */
  _resolveScalingToken(token, target, character, context = null) {
    const level = character?.level || 1;
    switch (token) {
      case 'characterLevel':
        return level;
      case 'classLevel': {
        if (!target) return 0;
        const cls = (character?.classes || []).find(
          c => String(c.className || '').toLowerCase() === String(target).toLowerCase()
        );
        return cls?.level || 0;
      }
      case 'proficiencyBonus':
        return Math.ceil(level / 4) + 1;
      case 'proficiencyTier':
        return Math.min(4, Math.ceil(level / 4));
      case 'spellSlotLevelExpended':
        return context?.spellSlotLevel || 0;
      case 'resourceSpent': {
        if (!target) return 0;
        return context?.resourceSpent?.[target] || 0;
      }
      // Ability modifier tokens — delegate to existing _getValue string branch
      case 'str_modifier':
      case 'dex_modifier':
      case 'con_modifier':
      case 'int_modifier':
      case 'wis_modifier':
      case 'cha_modifier':
        return this._getValue(token, false, character);
      default:
        return 0;
    }
  }

  _getValue(value, preserveDice = false, character = null) {
    // [SUB-PLAN 3D-S2 — 2026-05-28]: Structured expression object
    // Shape: { token, target?, operator?, operand?, modifier? }
    // Produced by FormulaPicker mode='structured'. Evaluates at calc-time.
    if (value !== null && typeof value === 'object' && !Array.isArray(value) && value.token) {
      const base = this._resolveScalingToken(value.token, value.target || null, character, null);
      if (!value.operator) return base;
      const operand = typeof value.operand === 'number' ? value.operand : 1;
      switch (value.operator) {
        case 'multiply':
          return Math.floor(base * operand);
        case 'divide': {
          if (operand === 0) return 0;
          const raw = base / operand;
          return value.modifier === 'ceil' ? Math.ceil(raw) : Math.floor(raw);
        }
        case 'add':
          return base + operand;
        default:
          return base;
      }
    }

    // Ability modifier formulas: 'str_modifier', 'dex_modifier', ..., 'cha_modifier'
    // Character stores scores as full names (character.charisma), formula uses 3-letter codes (cha)
    if (typeof value === 'string' && value.endsWith('_modifier')) {
      const abbrev = value.replace('_modifier', '');
      const abilityMap = {
        str: 'strength', dex: 'dexterity', con: 'constitution',
        int: 'intelligence', wis: 'wisdom', cha: 'charisma'
      };
      const abilityKey = abilityMap[abbrev] || abbrev; // fall back to raw key if not a standard abbrev
      const score = character?.[abilityKey] || 10;
      return Math.floor((score - 10) / 2);
    }

    // Proficiency bonus formula
    if (value === 'proficiency_bonus') {
      const level = character?.level || 1;
      return Math.ceil(level / 4) + 1;
    }

    // [Phase B / #042 — MAY 2026]: Rage damage bonus formula, data-block driven.
    // [WHY]: Pre-fix, this branch hardcoded the +2/+3/+4 ladder at L1/L9/L16
    //   inline. The ladder now lives on the feature definition itself
    //   (SRD_CLASS_FEATURES.Barbarian.Rage.rageDamageByLevel) and the
    //   resolver looks it up via resolveByLevelTable. PHB p.48 source.
    //   Kept the 'rage_damage_bonus' formula key so the effects-array entry
    //   on the feature stays compact ({ type:'damageBonus', value:'rage_damage_bonus' }).
    if (value === 'rage_damage_bonus' && character) {
      const barbarian = (character.classes || []).find(c => c.className === 'Barbarian');
      const lvl = barbarian?.level || 0;
      const table = SRD_CLASS_FEATURES?.Barbarian?.Rage?.rageDamageByLevel;
      const tier = resolveByLevelTable(table, lvl);
      return tier ?? 0;
    }

    // Handle dice notation (e.g., '1d4', '2d6', '1d8')
    if (typeof value === 'string' && /^\d+d\d+$/i.test(value)) {
      if (preserveDice) {
        return value; // Return dice string as-is for dice bonuses
      } else {
        // Convert to average value for numeric calculations
        // Example: 1d4 = (1+4)/2 * 1 = 2.5 → 2
        const [count, sides] = value.split('d').map(Number);
        const average = Math.floor(count * (sides + 1) / 2);
        return average;
      }
    }

    // [Phase 4b bucket 5 — APR 2026]: Multiplier formulas — characterLevel*N, <ability>Modifier*N,
    // and <className>Level*N (e.g., 'characterLevel*2' for Tough = +2 HP per level).
    if (typeof value === 'string' && value.includes('*') && character) {
      const match = value.match(/^([A-Za-z_]+)\s*\*\s*(\d+(?:\.\d+)?)$/);
      if (match) {
        const term = match[1];
        const multiplier = parseFloat(match[2]);
        let base = 0;

        if (term === 'characterLevel' || term === 'character_level') {
          base = character.level || 0;
        } else if (term.endsWith('Modifier')) {
          // e.g., 'constitutionModifier' → CON mod
          const abilityName = term.replace(/Modifier$/, '').toLowerCase();
          const score = character[abilityName];
          if (typeof score === 'number') {
            base = Math.floor((score - 10) / 2);
          }
        } else if (term.endsWith('Level')) {
          // e.g., 'BarbarianLevel' or 'barbarian_level' → that class's level
          const className = term.replace(/Level$/, '');
          const classEntry = (character.classes || []).find(c =>
            String(c.className || '').toLowerCase() === className.toLowerCase()
          );
          base = classEntry?.level || 0;
        } else if (term === 'proficiencyBonus' || term === 'proficiency_bonus') {
          base = Math.ceil((character.level || 1) / 4) + 1;
        }

        return Math.floor(base * multiplier);
      }
      // Unknown formula shape — log + return 0 (matches prior behavior for unknown formulas)
      console.warn('[EffectRegistry] Unrecognized multiplier formula:', value);
      return 0;
    }

    // [Phase 4b bucket 3 — APR 2026]: Dice-formula resolver — <term>/<divisor>:d<sides>:<rounding>
    // [WHY]: Sneak Attack scales with Rogue level (1d6 → 10d6); Hunter Colossus Slayer = 1d8;
    // similar features need data-driven dice scaling. Producer: damageBonus + add_dice operation.
    // Consumer: combat panel aggregates dice on hit (UI separate ticket BC-3).
    // Examples:
    //   'rogueLevel/2:d6:roundUp' at Rogue 9 → '5d6' (preserveDice) or 17 (numeric average)
    //   'characterLevel/4:d8:roundDown' at level 11 → '2d8'
    //   'rangerLevel/2:d6:roundUp' at Ranger 5 → '3d6'
    if (typeof value === 'string' && value.includes(':d') && character) {
      const m = value.match(/^([A-Za-z_]+)\/(\d+):d(\d+):(roundUp|roundDown)$/);
      if (m) {
        const [, term, divisorStr, sidesStr, rounding] = m;
        const divisor = parseInt(divisorStr, 10);
        const sides = parseInt(sidesStr, 10);
        let base = 0;

        if (term === 'characterLevel' || term === 'character_level') {
          base = character.level || 0;
        } else if (term.endsWith('Level')) {
          // e.g., 'rogueLevel' → that class's level
          const className = term.replace(/Level$/, '');
          const classEntry = (character.classes || []).find(c =>
            String(c.className || '').toLowerCase() === className.toLowerCase()
          );
          base = classEntry?.level || 0;
        }

        if (divisor <= 0 || sides <= 0 || base < 0) {
          console.warn('[EffectRegistry] Invalid dice formula values:', value);
          return preserveDice ? '0d0' : 0;
        }

        const count = rounding === 'roundUp'
          ? Math.ceil(base / divisor)
          : Math.floor(base / divisor);

        if (count < 1) {
          return preserveDice ? '0d0' : 0;
        }
        return preserveDice
          ? `${count}d${sides}`
          : Math.floor(count * (sides + 1) / 2);
      }
      console.warn('[EffectRegistry] Unrecognized dice formula:', value);
      return preserveDice ? '0d0' : 0;
    }

    // [ESR-004 FIX — MAY 2026]: Boolean inputs short-circuit before parseInt.
    //   Pre-fix: `_getValue(true)` returned 0 because parseInt(true) === NaN
    //   and `NaN || 0` is 0. Production code worked because every value:true
    //   registration paired with operation:'grant' / 'remove_disadvantage'
    //   which short-circuits in getEffectiveValue. But a future CW author
    //   might author `{type: 'ac', operation: 'add', value: true}` (mistyped
    //   from "always +1") and silently get +0. Now boolean true → 1, false → 0.
    if (value === true) return 1;
    if (value === false) return 0;
    if (value === 'true') return 1;
    if (value === 'false') return 0;
    return typeof value === 'number' ? value : parseInt(value) || 0;
  }

  _getSourceDisplayName(type, id, inventory = null) {
    // Convert source IDs to readable names
    const typeNames = {
      feat: 'Feat',
      item: 'Item',
      spell: 'Spell',
      condition: 'Condition',
      class: 'Class',
      race: 'Race',
      buff: 'Buff'
    };

    // [NULL SAFETY - NOV 17, 2025]: Guard against undefined id
    if (!id) {
      return typeNames[type] || type || 'Unknown';
    }

    // [NS-AC-source-label-name 2026-05-31]: item sources register as
    // "item:<item.id>:<slot>", so the split id is the inventory item's id —
    // an opaque key for custom items. Resolve the player-facing item.name when
    // the inventory is available (auditable for SRD ids, readable for custom).
    if (type === 'item' && Array.isArray(inventory) && inventory.length) {
      const match = inventory.find(it => it && String(it.id) === String(id));
      if (match && match.name) {
        return `${typeNames[type]}: ${match.name}`;
      }
    }

    // Convert snake_case to Title Case
    const name = id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    return `${typeNames[type] || type}: ${name}`;
  }

  _getCached(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.value;
    }
    return null;
  }

  _setCached(key, value) {
    this.cache.set(key, {
      value,
      timestamp: Date.now()
    });
  }

  /**
   * Check if registering this spell would violate D&D 5e stacking rules
   * [PHASE 2.2 - NOV 15, 2025]: D&D 5e spell stacking prevention
   * RULE: PHB p.205 - "The effects of the same spell cast multiple times don't combine, however the effects
   *       of different spells add together. The effects of multiple castings of the same spell or effect
   *       on a target don't stack; instead, the most potent effect applies while the durations overlap."
   *
   * @param {Object} metadata - New spell metadata {spellName, casterName, level, duration}
   * @returns {Object} { allowed: boolean, reason: string, conflictingSource: string, conflictingMeta: Object }
   */
  _checkSpellStacking(metadata) {
    if (!metadata?.spellName) {
      return { allowed: true }; // Not a spell, no stacking rules apply
    }

    // Find existing effects from the same spell
    for (const [sourceId, sourceMeta] of this.metadata.entries()) {
      if (sourceMeta.spellName === metadata.spellName) {
        // Found duplicate spell - D&D 5e rule: Same spell doesn't stack
        const casterInfo = sourceMeta.casterName ? ` (cast by ${sourceMeta.casterName})` : '';
        const levelInfo = sourceMeta.level ? ` at level ${sourceMeta.level}` : '';

        return {
          allowed: false,
          reason: `${metadata.spellName} is already active${casterInfo}${levelInfo}`,
          conflictingSource: sourceId,
          conflictingMeta: sourceMeta
        };
      }
    }

    return { allowed: true };
  }

  _setupEventListeners() {
    // Clear cache on relevant events
    systemBus.on(EVENTS.CHARACTER_LOADED, () => this.clear());
    systemBus.on(EVENTS.CHARACTER_RESET, () => this.clear());
    systemBus.on(EVENTS.STATS_CHANGED, () => this.clearCache());
    systemBus.on(EVENTS.EQUIPMENT_CHANGED, () => this.clearCache());
    systemBus.on(EVENTS.FEAT_ADDED, () => this.clearCache());
    systemBus.on(EVENTS.FEAT_REMOVED, () => this.clearCache());
  }
}

// [TDZ BUG FIX - NOV 17, 2025]: Changed from `const` to `var` to fix production minifier TDZ error
// [WHY]: Module-level `const` with instantiation can cause TDZ in minified bundles
// [CAUSE]: Webpack/minifier may reorder code, `var` is hoisted and has no TDZ
// Export singleton instance
var effectRegistry = new EffectRegistry();

export default effectRegistry;
export { effectRegistry, EffectRegistry };